import { isAfter, parseISO, format } from "date-fns";

export interface LabDate {
  id: string;
  run_id: string;
  lab_number: number;
  date: string;
}

export interface RunWithLabDates {
  id: string;
  name: string;
  active: boolean;
  lab_dates?: LabDate[];
  series?: {
    total_labs?: number;
    [key: string]: any; // Allow additional properties
  };
}

/**
 * Determines if a run is completed based on its lab dates
 * A run is considered "past" if:
 * 1. The last scheduled lab date has passed, OR
 * 2. The run is manually marked as inactive (active: false)
 */
export function isRunCompleted(run: {
  active: boolean;
  lab_dates?: LabDate[];
  [key: string]: any;
}): boolean {
  console.log(`isRunCompleted - Checking run:`, {
    name: run.name || "unnamed",
    active: run.active,
    lab_dates_count: run.lab_dates?.length || 0,
    lab_dates: run.lab_dates,
  });

  // If manually marked as inactive, consider it past
  if (!run.active) {
    console.log(`isRunCompleted - Run is inactive, marking as completed`);
    return true;
  }

  // If no lab dates are scheduled, fall back to the active flag
  if (!run.lab_dates || run.lab_dates.length === 0) {
    console.log(
      `isRunCompleted - No lab dates, using active flag: ${!run.active}`
    );
    return !run.active;
  }

  // Find the latest lab date
  const latestLabDate = run.lab_dates
    .filter((labDate) => labDate.date) // Only consider dates that are set
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];

  // If no valid dates found, fall back to active flag
  if (!latestLabDate) {
    console.log(
      `isRunCompleted - No valid lab dates found, using active flag: ${!run.active}`
    );
    return !run.active;
  }

  // Check if the latest lab date has passed (is before today)
  const today = new Date();
  today.setHours(23, 59, 59, 999); // End of today to ensure labs happening today are not considered "past"

  const isCompleted = isAfter(today, parseISO(latestLabDate.date));
  console.log(
    `isRunCompleted - Latest lab date: ${latestLabDate.date}, Today: ${
      today.toISOString().split("T")[0]
    }, Is completed: ${isCompleted}`
  );

  return isCompleted;
}

/**
 * Categorizes runs into current and past based on lab completion status
 */
export function categorizeRuns<
  T extends {
    active: boolean;
    lab_dates?: LabDate[];
    [key: string]: any;
  }
>(
  runs: T[]
): {
  current: T[];
  past: T[];
} {
  const current: T[] = [];
  const past: T[] = [];

  runs.forEach((run) => {
    if (isRunCompleted(run)) {
      past.push(run);
    } else {
      current.push(run);
    }
  });

  return { current, past };
}

/**
 * Gets the status label for a run
 */
export function getRunStatusLabel(run: {
  active: boolean;
  lab_dates?: LabDate[];
  [key: string]: any;
}): "Current" | "Past" {
  return isRunCompleted(run) ? "Past" : "Current";
}

/**
 * Sorts an array of day names in weekly order (Monday through Sunday)
 */
function sortDaysOfWeek(days: string[]): string[] {
  const dayOrder = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  return days.sort((a, b) => {
    const indexA = dayOrder.indexOf(a);
    const indexB = dayOrder.indexOf(b);
    return indexA - indexB;
  });
}

/**
 * Sorts and normalizes day_of_week array for consistent storage
 * Use this when saving run data to ensure days are always in correct order
 */
export function normalizeDaysOfWeek(days: string[]): string[] {
  return sortDaysOfWeek(days);
}

/**
 * Formats day_of_week array for display
 * Handles both legacy single day strings and new array format
 */
export function formatDaysOfWeek(dayOfWeek: string | string[]): string {
  // Handle legacy single day format
  if (typeof dayOfWeek === "string") {
    return dayOfWeek;
  }

  // Handle new array format
  if (Array.isArray(dayOfWeek)) {
    if (dayOfWeek.length === 0) {
      return "No days scheduled";
    }

    if (dayOfWeek.length === 1) {
      return `${dayOfWeek[0]}s`;
    }

    // Sort days in weekly order before processing
    const sortedDays = sortDaysOfWeek(dayOfWeek);

    // Check for common patterns
    const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      weekends = ["Saturday", "Sunday"],
      allDays = [...weekdays, ...weekends];

    // Check if it's all weekdays
    if (
      sortedDays.length === 5 &&
      weekdays.every((day) => sortedDays.includes(day))
    ) {
      return "Weekdays";
    }

    // Check if it's all days
    if (
      sortedDays.length === 7 &&
      allDays.every((day) => sortedDays.includes(day))
    ) {
      return "Daily";
    }

    // Check if it's weekends
    if (
      sortedDays.length === 2 &&
      weekends.every((day) => sortedDays.includes(day))
    ) {
      return "Weekends";
    }

    // For other combinations, join with commas and "and" before the last item
    if (sortedDays.length === 2) {
      return sortedDays.join(" and ");
    }

    const lastDay = sortedDays[sortedDays.length - 1];
    const otherDays = sortedDays.slice(0, -1);
    return `${otherDays.join(", ")} and ${lastDay}`;
  }

  return "No days scheduled";
}

/**
 * Gets the date range for a run based on its lab dates
 * Returns a formatted string showing the start and end dates
 */
export function getRunDateRange(run: {
  lab_dates?: LabDate[];
  [key: string]: any;
}): string {
  if (!run.lab_dates || run.lab_dates.length === 0) {
    return "No dates scheduled";
  }

  // Filter out lab dates without dates and sort them
  const validLabDates = run.lab_dates
    .filter((labDate) => labDate.date)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (validLabDates.length === 0) {
    return "No dates scheduled";
  }

  const firstDate = parseISO(validLabDates[0].date);
  const lastDate = parseISO(validLabDates[validLabDates.length - 1].date);

  // If only one lab date
  if (validLabDates.length === 1) {
    return format(firstDate, "MMM d, yyyy");
  }

  // If start and end are in the same year
  if (firstDate.getFullYear() === lastDate.getFullYear()) {
    // If start and end are in the same month
    if (firstDate.getMonth() === lastDate.getMonth()) {
      return `${format(firstDate, "MMM d")} - ${format(lastDate, "d, yyyy")}`;
    } else {
      return `${format(firstDate, "MMM d")} - ${format(
        lastDate,
        "MMM d, yyyy"
      )}`;
    }
  } else {
    return `${format(firstDate, "MMM d, yyyy")} - ${format(
      lastDate,
      "MMM d, yyyy"
    )}`;
  }
}
